/**
 * Génération message de diffusion catalogue (miroir client de shared/catalogBroadcastMessage.cjs).
 */
import { FR_DEPARTEMENTS } from "@/lib/departements";

const DEPT_NAME_BY_CODE = Object.fromEntries(FR_DEPARTEMENTS.map((d) => [d.code, d.name]));

function deptNameByCode(code) {
  return DEPT_NAME_BY_CODE[String(code || "").trim()] || null;
}

function normalizeDeptChampKey(nom) {
  return String(nom || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function isDepartementChampKey(nom) {
  const k = normalizeDeptChampKey(nom);
  return k === "departement" || k === "département" || k.includes("departement");
}

function parseDepartmentCodes(text) {
  const t = String(text ?? "").trim();
  if (!t) return [];
  if (/^\d{2,3}$/.test(t)) return [t];
  const codes = [];
  const parts = t.split(/[\s,;/\-–—·]+/).map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    if (/^\d{2,3}$/.test(p)) codes.push(p);
    else {
      const m = p.match(/\b(\d{2,3})\b/g);
      if (m) codes.push(...m);
    }
  }
  return [...new Set(codes)];
}

function dossierCustomFields(dossier) {
  return dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
    ? dossier.custom_fields
    : {};
}

function findDepartementFieldRaw(dossier) {
  const cf = dossierCustomFields(dossier);
  for (const k of Object.keys(cf)) {
    if (!isDepartementChampKey(k)) continue;
    const raw = String(cf[k] ?? "").trim();
    if (raw) return raw;
  }
  return null;
}

function collectDepartmentCodesForDossier(dossier) {
  const codes = new Set();
  const fromField = findDepartementFieldRaw(dossier);
  if (fromField) parseDepartmentCodes(fromField).forEach((c) => codes.add(c));
  const loc = dossier?.location;
  if (loc != null && String(loc).trim()) parseDepartmentCodes(loc).forEach((c) => codes.add(c));
  return [...codes];
}

function normalizeDossierType(type) {
  const t = String(type || "").toLowerCase().trim();
  if (t === "recherche" || t === "achat" || t === "buy") return "recherche";
  return "vente";
}

function isMarketplaceOnline(dossier) {
  if (dossier?.hors_saas) return false;
  const st = dossier?.status == null ? "online" : String(dossier.status).trim().toLowerCase();
  return st === "online";
}

export function parseProduitIdsFilter(raw) {
  if (raw == null || raw === "" || raw === "all") return [];
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean);
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function sortDeptCodes(codes) {
  return [...codes].sort((a, b) => {
    const na = String(a).replace(/\D/g, "") || String(a);
    const nb = String(b).replace(/\D/g, "") || String(b);
    if (na.length !== nb.length) return na.length - nb.length;
    return na.localeCompare(nb, "fr", { numeric: true });
  });
}

function formatDeptCompact(codes, { withNames = true } = {}) {
  const sorted = sortDeptCodes(codes);
  if (!sorted.length) return "départements à préciser sur la fiche";
  return sorted
    .map((code) => {
      const name = withNames ? deptNameByCode(code) : null;
      return name ? `${code} (${name})` : code;
    })
    .join(", ");
}

function formatDateFr(date = new Date()) {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Paris",
  }).format(date instanceof Date ? date : new Date(date));
}

function capitalizeFirstLetter(s) {
  const t = String(s || "").trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function formatDeptForBroadcast(codes) {
  const sorted = sortDeptCodes(codes);
  if (!sorted.length) return "Dept: à préciser sur la fiche";
  return `Dept: ${sorted.join(", ")}`;
}

function formatListingAvailability(count, minPrice) {
  const n = Number(count) || 0;
  const label = n === 1 ? "1 RDV dispo" : `${n} RDV dispo`;
  const priceFr = formatPriceEur(minPrice);
  return priceFr ? `${label} dès ${priceFr}` : label;
}

function formatPriceEur(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  return `${Math.round(v)} €`;
}

export function buildScopeLabel(filters, produitsById) {
  const secteur = String(filters.secteur || "").trim();
  const pids = parseProduitIdsFilter(filters.produitIds);
  if (pids.length === 1) {
    const p = produitsById[pids[0]];
    if (p?.nom) return p.nom;
  }
  if (pids.length > 1) {
    const names = pids
      .map((id) => produitsById[id]?.nom)
      .filter(Boolean)
      .slice(0, 3);
    if (names.length) return names.join(", ") + (pids.length > 3 ? "…" : "");
  }
  if (secteur && secteur !== "all") return secteur;
  return "Tout le catalogue";
}

function dossierMatchesFilters(dossier, produitsById, filters) {
  if (!isMarketplaceOnline(dossier)) return false;
  if (normalizeDossierType(dossier.type) !== "vente") return false;
  const produit = produitsById[String(dossier.produit_id || "")];
  const secteur = String(produit?.secteur || dossier.secteur || "").trim();
  const secteurFilter = String(filters.secteur || "").trim();
  if (secteurFilter && secteurFilter !== "all" && secteur !== secteurFilter) return false;
  const pids = parseProduitIdsFilter(filters.produitIds);
  if (pids.length > 0 && !pids.includes(String(dossier.produit_id || ""))) return false;
  return true;
}

export function buildCatalogBroadcastMessage({ dossiers = [], produitsById = {}, filters = {}, options = {} }) {
  const siteUrl = String(options.siteUrl || "https://rdvmarket.fr").replace(/\/$/, "");
  const marketplaceUrl = `${siteUrl}/marketplace`;
  const dateLabel =
    options.dateLabel || capitalizeFirstLetter(formatDateFr(options.date || new Date()));
  const scopeLabel = options.scopeLabel || buildScopeLabel(filters, produitsById);

  const filtered = (dossiers || []).filter((d) => dossierMatchesFilters(d, produitsById, filters));
  const groupsMap = new Map();

  for (const d of filtered) {
    const pid = String(d.produit_id || "").trim() || "__sans_produit__";
    const produit = produitsById[pid] || produitsById[d.produit_id];
    const productName = String(produit?.nom || d.secteur || "Autre produit").trim() || "Autre produit";
    const secteur = String(produit?.secteur || d.secteur || "").trim();

    if (!groupsMap.has(pid)) {
      groupsMap.set(pid, {
        produit_id: pid === "__sans_produit__" ? null : pid,
        productName,
        secteur,
        deptCodes: new Set(),
        listingCount: 0,
        minPrice: null,
      });
    }
    const g = groupsMap.get(pid);
    g.listingCount += 1;
    collectDepartmentCodesForDossier(d).forEach((c) => g.deptCodes.add(c));
    const price = Number(d.price);
    if (Number.isFinite(price) && price > 0) {
      g.minPrice = g.minPrice == null ? price : Math.min(g.minPrice, price);
    }
  }

  const groups = [...groupsMap.values()].sort((a, b) =>
    a.productName.localeCompare(b.productName, "fr", { sensitivity: "base" }),
  );

  const lines = [];
  lines.push("🚀 Rendez-vous qualifiés disponibles sur RdvMarket");
  lines.push(dateLabel);
  lines.push("");
  lines.push("💳 Principe simple :");
  lines.push("Vous validez le RDV après l’appel → vous payez.");
  lines.push(
    "Vous refusez → remboursement si motif valable (enregistrement + analyse IA à l’appui).",
  );
  lines.push("");

  if (groups.length === 0) {
    lines.push("Aucune offre en ligne pour cette sélection pour le moment.");
    lines.push("Revenez bientôt ou élargissez le filtre (secteur / produit).");
  } else {
    for (const g of groups) {
      lines.push(`▸ ${g.productName}`);
      if (g.secteur) lines.push(g.secteur);
      lines.push(formatDeptForBroadcast([...g.deptCodes]));
      lines.push(formatListingAvailability(g.listingCount, g.minPrice));
      lines.push("");
    }
  }

  lines.push("👉 Voir toutes les fiches et réserver sur:");
  lines.push(marketplaceUrl);
  lines.push("");
  lines.push("RdvMarket.fr — la marketplace des rendez-vous qualifiés");

  const message = lines.join("\n").trim();
  const totalListings = groups.reduce((s, g) => s + g.listingCount, 0);

  return {
    message,
    groups: groups.map((g) => ({
      produit_id: g.produit_id,
      productName: g.productName,
      secteur: g.secteur,
      departments: sortDeptCodes([...g.deptCodes]),
      listingCount: g.listingCount,
      minPrice: g.minPrice,
    })),
    meta: {
      dateLabel,
      marketplaceUrl,
      siteUrl,
      scopeLabel,
      secteur: filters.secteur || "all",
      produitIds: parseProduitIdsFilter(filters.produitIds),
      productCount: groups.length,
      totalListings,
      generatedAt: new Date().toISOString(),
    },
  };
}
